import * as React from 'react';
import classnames from 'classnames';

import * as UI from 'core/ui';
import { OrderingFacade } from 'pos/facades';
import * as Images from 'pos/Images';

import { Command, Cordova } from 'pos/common';
import * as SalesStore from '../SalesStore';
import { Result, handleServerErrors } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { RewardDTO, LoyaltyProgramMemberDTO, Permission, Res } from 'core/api';
import { QRReader } from 'pos/services';
import { Flex, Cols, Col } from 'core/ui';
import { BarcodeReader, Toaster } from 'core/ui-next';
import { Message, Modal } from 'core/ui-next/modal';

type State = {
	currentMemberId: string;
	errorMsg: string,
	loyaltyProgramMember: Nullable<LoyaltyProgramMemberDTO>;
	selectedRewards: RewardDTO[];
};

type CardProps = {
	reward: RewardDTO;
	selected: boolean;
	onSelected: (reward: RewardDTO) => void;
};

async function searchMember(memberId: string): Promise<Result<LoyaltyProgramMemberDTO>> {
	return await handleServerErrors(async () => {
		return await OrderingFacade.searchMember(memberId);
	}, {
		onDomainError: async ex => {
			Toaster.warn(Res.Msg.MSG_CANNOT_VALIDATE_MEMBER_ID(ex.message));
		},
		onIntegrationAuthorizationError: async ex => {
			Toaster.warn(Res.Msg.MSG_INTEGRATION_AUTHORIZATION_ERROR(ex.message));
		}
	});
}

const RewardCard = ({ reward, selected, onSelected }: CardProps) => {
	const cardClasses = classnames('card', { 'selected': selected });
	const starClasses = classnames('text-warning fa', { 'fa-star-o': !selected, 'fa-star': selected });

	return <div className={cardClasses} onClick={() => onSelected(reward)}>
		<div className='card-icon'><i className={starClasses}></i></div>
		<div>
			<div className='card-title'>{reward.Name}</div>
			<div className='card-description'>{reward.Description}</div>
		</div>
	</div>;
};

class AddLoyaltyInfo extends PosScreenClassComponent<Modal.Actions<LoyaltyProgramMemberDTO>, State> {

	private input = React.createRef<HTMLInputElement>();
	private disposeReader: () => void = () => { };

	protected getScreenId(): string {
		return 'ADD_LOYALTY_INFO';
	}

	protected getTitle(): string {
		return Res.Web.ADD_LOYALTY_INFO_VIEW();
	}

	protected getImage(): string | SvgIcon {
		return Images.AddLoyaltyInfo;
	}

	protected getSize(): ScreenSize {
		return 'medium';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			this.okButton(this.onOK)
		];
	}

	protected useKeypad(): boolean {
		return false;
	}

	createInitialState(_props: Modal.Actions<LoyaltyProgramMemberDTO>): Nullable<State> {
		return {
			currentMemberId: '',
			errorMsg: '',
			loyaltyProgramMember: null,
			selectedRewards: []
		};
	}

	onComponentDidMount(): void {
		this.disposeReader = BarcodeReader.start(this.onCodeRead);
	}

	onComponentWillUnmount(): void {
		this.disposeReader();
	}

	onRewardSelected = (reward: RewardDTO) => {
		this.state.selectedRewards.indexOf(reward) >= 0
			? this.setState({ selectedRewards: [...this.state.selectedRewards.filter(x => x !== reward)] })
			: this.setState({ selectedRewards: [...this.state.selectedRewards, reward] });
	};

	onOK = async () => {
		if (!this.state.loyaltyProgramMember) {
			await Message.show(Res.Msg.MSG_NO_LOYALTY_MEMBER_SELECTED());
			return;
		}

		const member = {
			...this.state.loyaltyProgramMember!,
			Rewards: this.state.selectedRewards
		};

		this.close(member);
	};

	searchMember = async (memberId: string) => {
		const result = await searchMember(memberId);
		if (result.ok)
			this.setState({ loyaltyProgramMember: result.value });
		else
			this.setState({ loyaltyProgramMember: null });
	};

	onCodeRead = async (memberId: string) => {
		this.setState({ currentMemberId: memberId, errorMsg: '' }, async () => await this.searchMember(memberId));
	};

	onSearch = async () => {
		const memberId = this.input.current!.value.trim();

		if (memberId === null || memberId === '') {
			this.setState({ errorMsg: Res.Web.ADD_LOYALTY_INFO_EMPTY_MEMBER_ID_ERROR(), loyaltyProgramMember: null });
			return;
		}

		await this.searchMember(memberId);
	};

	onReadQR = async () => {
		const qrResult = await QRReader.read();
		if (!qrResult.ok)
			return;

		await this.onCodeRead(qrResult.value);
	};

	protected getContent(): React.ReactNode {
		const { Button, ErrorBlock } = UI;

		const displayText = () => {
			return this.state.loyaltyProgramMember && <p className='m-sm m-b-md text-18 text-center' style={{ whiteSpace: 'pre' }}>
				{
					this.state.loyaltyProgramMember.DisplayText === ''
						? Res.Web.ADD_LOYALTY_INFO_EMPTY_MEMBER_LBL()
						: this.state.loyaltyProgramMember.DisplayText
				}
			</p >;
		};

		const rewardCards = () => {
			if (!this.state.loyaltyProgramMember)
				return null;

			const cards = this.state.loyaltyProgramMember.Rewards.map(r => {
				return <RewardCard key={r.RewardId} reward={r} selected={this.state.selectedRewards.indexOf(r) >= 0} onSelected={this.onRewardSelected} />;
			});

			return <div className='card-grid'>
				{cards}
			</div>;
		};

		const onChange = (ev: any) => {
			this.setState({
				currentMemberId: ev.target.value,
				errorMsg: ''
			});
		};

		const scanQRButton = Cordova.isCordovaNavigator()
			? <Button buttonStyle='info' className='m-l-sm' onClick={this.onReadQR}><i className='fa fa-qrcode m-r-xs' />{Res.Web.ADD_LOYALTY_INFO_SCAN_QR_BTN()}</Button>
			: null;

		return <Flex.Vertical>
			<Flex.Item className='m-t-sm text-center'>
				<Cols>
					<Col width='*'>
						<div className='input-group'>
							<input ref={this.input} id='memberId' className='form-control' value={this.state.currentMemberId} onChange={onChange} onKeyDown={UI.whenEnterKey(() => this.onSearch())} placeholder={Res.Web.ADD_LOYALTY_INFO_VIEW_IDENTIFIER_PLACEHOLDER()} />
							<span className='input-group-btn'>
								<Button buttonStyle='info' onClick={this.onSearch}><i className='fa fa-search' /></Button>
							</span>
						</div>
					</Col>
					<Col>
						{scanQRButton}
					</Col>
				</Cols>
				<div className='has-error'>
					<ErrorBlock msg={this.state.errorMsg} />
				</div>
			</Flex.Item>
			<Flex.Item size='*'>
				{displayText()}
				{rewardCards()}
			</Flex.Item>
		</Flex.Vertical >;
	}
}

declare global {
	interface CommandType {
		addLoyaltyInfo: () => Promise<Result>
	}
}

Command.register('addLoyaltyInfo', Preconditions.and(Preconditions.ensure(Permission.Ticket_AddLoyaltyInfo), Preconditions.ensureIsNotLite), async (): Promise<Result> => {
	const result = await Modal.show(AddLoyaltyInfo);
	if (!result.ok)
		return result;

	const member = result.value;
	await SalesStore.addLoyaltyInfo(member.MemberId, member.Rewards);

	return Result.ok();
});